import { useStableObject } from '@atlassian/post-office-context';
import { MessagePerformance } from '@post-office/message-performance';
import { MessageRendererProvider } from '@post-office/placement-contexts/message-renderer-provider';
import { MessageViewed } from '@post-office/placement-contexts/message-viewed-component';
import type { TriggerType } from '@post-office/universal-contracts/types';
import { isClientTriggeredMessage } from '@post-office/placement-contexts/utils/is-client-triggered-message';

import { createPlacementComponentV2 } from './create-placement-component';
import { type RuntimeParams } from './create-placement-component/types';
import { defaultPlacementMiddleware } from './default-placement-middleware';
import {
	MissingPlacementIdError,
	PlacementMessageError,
	PlacementSystemError,
	createPlacementMessageError,
	handlePlacementError,
	handlePlacementMessageError,
	handlePlacementMiddlewareError,
	handlePlacementRuntimeError,
} from './error';
import type {
	AnyBackendMessageStages,
	AnyBackendPlacementStage,
	AnyBackendPlacementStageInput,
	AnyBackendPlacementStages,
	AnyFrontendStages,
	AnyPlacementDefinition,
	AnyPlacementMessageStagesConfig,
	AnyProps,
	AnyValidatedBackendMessageStage,
	AnyValidatedBackendMessageStages,
	AnyValidatedStages,
	CreateBackendPlacementMessageExecuteConfig,
	CreateBackendPlacementMessageRunner,
	CreateFrontendPlacementMessageExecuteConfig,
	CreateFrontendPlacementMessageRunner,
	CreateMessageBackend,
	DefaultBackendMessageStages,
	DefaultFrontendStages,
	DefaultPlacementDefinition,
	MessagesRuntime,
	NoProps,
	PlacementBackend,
	PlacementConfig,
	PlacementDescription,
	PlacementKoaContext,
	PlacementLocation,
	PlacementMessage,
	PlacementMessageBackendFactory,
	PlacementMessageFrontendFactory,
	PlacementPostOfficeContext,
	PlacementRenderer,
	PlacementRestMiddleware,
	RequestContext,
} from './types';
import type { PlacementRouterState } from '../placement-router/types';
import { noValidation } from './util/message-helpers';
import {
	logRuntimeResult,
	messageTimedStage,
	placementRuntimeTimedStage,
	placementTimedStage,
} from './util/observability';
import { defaultBackendPlacementStages } from '../stages/placement/backend/default';
import { defaultBackendPlacementMessageStages } from '../stages/placement-message/backend/default';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useWaitForTrigger } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import type { PlacementDataEndpointBody } from '@atlassian/post-office-headless-api/hooks/usePlacementData';
import { useEffect } from 'react';

export const createPlacement = <T extends AnyPlacementDefinition = DefaultPlacementDefinition>(
	placementConfig: PlacementConfig,
): {
	createFrontend: <U extends AnyProps = NoProps>(
		placementRenderer: PlacementRenderer<T['frontend'], U>,
	) => PlacementMessageFrontendFactory<T['frontend'], U>;
	createBackend: (
		placementBackendParams?: PlacementBackend<T['backend']> | undefined,
	) => PlacementMessageBackendFactory<T['backend']>;
} => {
	return {
		createFrontend: createFrontend<T['frontend']>(placementConfig),
		createBackend: createBackend<T['backend']>(placementConfig),
	};
};

/*-- Frontend --*/

const createFrontend =
	<T extends AnyFrontendStages = DefaultFrontendStages>(placementConfig: PlacementConfig) =>
	<U extends AnyProps = NoProps>(
		placementRenderer: PlacementRenderer<T, U>,
	): PlacementMessageFrontendFactory<T, U> => ({
		createFrontendStage: (stage) => stage as never,

		createMessageFrontend: (stage) => (config) => [config.messageTemplateId, stage],

		createPlacementFrontendComponent: (config) => {
			const messageTemplateRouter = createFrontendPlacementMessageTemplateRouter({
				...placementConfig,
				...config,
			});

			return createPlacementComponentV2(
				placementConfig.placementId,
				placementRenderer(messageTemplateRouter),
				placementConfig.options,
			);
		},
	});

const createFrontendPlacementMessageRunner: CreateFrontendPlacementMessageRunner =
	({ messageTemplates, isMaybeI18nProviderEnabledCallback, ...config }) =>
	({ messageTemplateId, messageInstanceId, messageCategory, children, ...props }) => {
		const renderableStage = messageTemplates?.[messageTemplateId];

		// Headless messages are handled in-product by the consuming team (e.g. via usePlacementData).
		// They are registered in the backend but have no PlacementFrontend renderer — return null
		// silently without logging a warning.
		const isHeadless = config.headlessMessageTemplateIds?.includes(messageTemplateId);

		// This should never happen — a message cannot be both headless and have a renderer.
		// If it does, log an error but render anyway to avoid silently swallowing the message.
		useEffect(() => {
			if (isHeadless && renderableStage) {
				// eslint-disable-next-line no-console
				console.error(
					new PlacementMessageError({
						...placementLocation,
						...config,
					}),
					'Misconfiguration: message is registered as both headless and has a MessageFrontend renderer. Remove it from headlessMessageTemplateIds or remove the MessageFrontend export.',
				);
			}

			if (!isHeadless && !renderableStage) {
				// eslint-disable-next-line no-console
				console.error(
					new PlacementMessageError({ ...placementLocation, ...config }),
					'Misconfiguration: message is not registered as headless but has no MessageFrontend renderer. Register it as a headless message by reinstalling or add a MessageFrontend export.',
				);
			}
		}, [isHeadless, renderableStage]);

		const componentPropsStable = useStableObject(props);

		// Client-triggered messages: triggerType is preserved in context by backend hydration
		const triggerType = (props as { context?: { triggerType?: TriggerType } })?.context
			?.triggerType;
		const hasTrigger =
			FeatureGates.checkGate('post-office_enable_trigger-choreographing') &&
			isClientTriggeredMessage(triggerType);

		// Create waitForTrigger hook for triggered messages
		// Note: messageSuccessComponent is undefined here since isUFOEnabled={false}
		const { waitForTrigger } = useWaitForTrigger({
			hasTrigger,
			messageSuccessComponent: undefined,
			messageInstanceId,
			messageTemplateId,
			messageCategory,
		});

		const placementLocation: PlacementLocation = {
			messageTemplateId,
			messageInstanceId,
			placementLevel: 'Message',
			stageName: config?.stageName,
			placementId: config.placementId,
		};

		if (!renderableStage) {
			return null;
		}

		try {
			renderableStage.validate({ messageTemplateId, children, ...props }); // Will throw error if type validation fails
			const { Component } = renderableStage;

			return (
				<MessageRendererProvider
					{...componentPropsStable}
					messageTemplateId={messageTemplateId}
					messageInstanceId={messageInstanceId}
					messageCategory={messageCategory}
					isUFOEnabled={false} // TODO enable UFO for placement v2
					componentProps={componentPropsStable}
					waitForTrigger={waitForTrigger}
					isMaybeI18nProviderEnabledCallback={isMaybeI18nProviderEnabledCallback}
					placementId={config.placementId}
				>
					{/* Triggered messages don't need MessageViewed - validator handles it */}
					{!waitForTrigger && <MessageViewed />}
					<Component {...props} waitForTrigger={waitForTrigger}>
						{children}
					</Component>
				</MessageRendererProvider>
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(createPlacementMessageError(error));

			return null;
		}
	};

const createFrontendPlacementMessageTemplateRouter = <T extends AnyFrontendStages>(
	config: CreateFrontendPlacementMessageExecuteConfig<T>,
) => {
	const messageTemplateLookup = createMessageTemplateByStageLookup(config);

	return Object.entries(messageTemplateLookup).reduce(
		(base, [stageName, messageTemplates]) => ({
			...base,
			[stageName]: createFrontendPlacementMessageRunner({
				messageTemplates,
				stageName,
				placementId: config.placementId,
				isMaybeI18nProviderEnabledCallback: config.isMaybeI18nProviderEnabledCallback,
				headlessMessageTemplateIds: config.headlessMessageTemplateIds,
			}),
		}),
		{},
	) as T;
};

/*-- Backend --*/

/**
 * Creates a message backend factory entry for a given set of stages.
 * This is placement-agnostic — it does not require a placementId.
 * Used internally by createPlacement().backend() and exported for
 * DefaultHeadlessPlacementBackend in public/index.ts.
 */
export const createPlacementMessageBackend =
	(stages: () => AnyValidatedBackendMessageStages) =>
	(messageBackendConfig: {
		messageTemplateId: string;
	}): [string, AnyValidatedBackendMessageStages] => {
		const stagesWithDefaults = applyDefaultMessageBackendStages(stages);
		return [messageBackendConfig.messageTemplateId, stagesWithDefaults];
	};

const createBackend =
	<T extends AnyBackendMessageStages = DefaultBackendMessageStages>(
		placementConfig: PlacementConfig,
	) =>
	(placementBackendParams?: PlacementBackend<T>): PlacementMessageBackendFactory<T> => ({
		createBackendStage: (stage) => stage as never,

		createMessageBackend: (stages) =>
			createPlacementMessageBackend(stages) as ReturnType<CreateMessageBackend<T>>,

		createPlacementBackend: (placementBackendConfig) => (runtime) => {
			const placementId = placementConfig.placementId;

			if (!placementId) {
				throw new MissingPlacementIdError();
			}

			const placementBackendStages: AnyPlacementMessageStagesConfig =
				placementBackendParams?.stages ?? defaultBackendPlacementStages;

			const placementStages = createBackendPlacementRunners(placementBackendStages);

			const messagesStages = createBackendPlacementMessageTemplateRouter({
				...placementConfig,
				...placementBackendConfig,
			});

			const middleware = placementBackendParams?.customMiddleware ?? defaultPlacementMiddleware;

			const statelessMiddleware = middleware({
				...placementConfig,
				...placementBackendParams?.params,
			});

			const statelessMessagesRuntime = runtime({
				messagesStages,
				placementStages,
				placementId,
			});

			const withRequestMetadata = (ctx: PlacementKoaContext['ctx']) => {
				const includePlacementMetadata = ctx.header?.['x-include-request-metadata'] === 'true';

				if (includePlacementMetadata) {
					// Cast is safe: addRequestContext and addLocation middleware run before this code.
					const state = ctx.state as PlacementRouterState & {
						requestContext: { location: RequestContext['location'] };
					};
					(ctx.body as Record<string, unknown>).requestMetadata = {
						...describe(),
						graph: FeatureGates.checkGate('post_office_enable-visualization')
							? state.requestContext.location.getGraph()
							: undefined,
					};
				}
			};

			const buildPlacementContext = (
				ctx: PlacementKoaContext['ctx'],
				next: PlacementKoaContext['next'],
			) => {
				const koa: PlacementKoaContext = {
					ctx,
					next,
				};

				// Cast is safe: addRequestContext and addLocation middleware run before this code.
				const state = ctx.state as PlacementRouterState & {
					requestContext: { location: RequestContext['location'] };
				};
				const request = state.requestContext.location.tag({
					placementId: placementConfig.placementId,
				});

				const messagesRuntime: MessagesRuntime = async (config) => {
					// config?.request is already typed with location; request is from buildPlacementContext which has it.
					const configuredRequest = (config?.request ?? request).location.tag({
						placementLevel: 'Runtime',
					});

					if (!configuredRequest) {
						throw new PlacementSystemError({
							message: 'request context not available at runtime call',
						});
					}

					const contexts = { koa, request: configuredRequest };

					const combinedParams: RuntimeParams = {
						...placementBackendParams?.params,
						...config?.params,
					};

					try {
						const runtimeStartTime = performance.now();
						const result =
							await placementRuntimeTimedStage(statelessMessagesRuntime)(contexts)(combinedParams);
						// Note: runtimeLatencyInMs includes placementRuntimeTimedStage's own finally-block
						// overhead (histogram stop, gate check, "Finished stage" log), so it will be
						// marginally higher than the latencyInMs emitted inside that stage for the same request.
						const runtimeLatencyInMs = performance.now() - runtimeStartTime;

						logRuntimeResult(contexts)({ result, latencyInMs: runtimeLatencyInMs });

						return result;
					} catch (error) {
						// contexts.request is guaranteed to have location from buildPlacementContext.
						throw handlePlacementRuntimeError(contexts.request)(error);
					}
				};

				const postOffice: PlacementPostOfficeContext = {
					messagesRuntime,
				};

				return { koa, request, postOffice, messagesRuntime };
			};

			const koaMiddleware: PlacementRestMiddleware = async (ctx, next) => {
				const { koa, request, postOffice } = buildPlacementContext(ctx, next);

				// Hoist middlewareRequest before try so it is accessible in finally.
				const middlewareRequest: RequestContext = request.location.tag({
					placementLevel: 'Middleware',
				});
				try {
					await statelessMiddleware({
						koa,
						postOffice,
						request: middlewareRequest,
						placement: placementConfig,
					});
				} catch (error) {
					// request is guaranteed to have location from buildPlacementContext.
					throw handlePlacementMiddlewareError(request)(error);
				} finally {
					// Stop the timer in finally so durationMs is recorded whether the
					// middleware succeeded or threw — making failed stages distinguishable
					// from untimed stages in the execution graph.
					if (FeatureGates.checkGate('post_office_enable-visualization')) {
						middlewareRequest.location.stopTimer();
					}
					withRequestMetadata(ctx);
				}
			};

			const dataMiddleware: PlacementRestMiddleware = async (ctx, next) => {
				const { request, messagesRuntime } = buildPlacementContext(ctx, next);

				// Hoist middlewareRequest before try so it is accessible in finally.
				const middlewareRequest: RequestContext = request.location.tag({
					placementLevel: 'Middleware',
				});
				try {
					const result: PlacementDataEndpointBody<{
						messageTemplateId: string;
						context: Record<string, unknown>;
					}> = await messagesRuntime({
						request: middlewareRequest,
					});

					ctx.body = result;
					ctx.status = 200;
				} catch (error) {
					throw handlePlacementMiddlewareError(request)(error);
				} finally {
					// Stop the timer in finally so durationMs is recorded whether the
					// middleware succeeded or threw — making failed stages distinguishable
					// from untimed stages in the execution graph.
					if (FeatureGates.checkGate('post_office_enable-visualization')) {
						middlewareRequest.location.stopTimer();
					}
					withRequestMetadata(ctx);
				}
			};

			const describe = (): PlacementDescription => {
				const messageTemplates = placementBackendConfig.messageTemplates.map(([id, stages]) => {
					return { id, stages: Object.keys(stages) };
				});

				const placementId = placementConfig.placementId;

				return {
					placementId,
					messageTemplates,
				};
			};

			const standaloneRuntime: MessagesRuntime = async (config) => {
				if (!config?.request) {
					throw new PlacementSystemError({
						message: 'request is required when calling runtime directly',
					});
				}

				const request = config.request.location.tag({
					placementId: placementConfig.placementId,
					placementLevel: 'Runtime',
				});

				const combinedParams: RuntimeParams = {
					...placementBackendParams?.params,
					...config?.params,
				};

				try {
					const runtimeStartTime = performance.now();
					const result = await placementRuntimeTimedStage(statelessMessagesRuntime)({ request })(
						combinedParams,
					);
					const runtimeLatencyInMs = performance.now() - runtimeStartTime;

					logRuntimeResult({ request })({ result, latencyInMs: runtimeLatencyInMs });

					return result;
				} catch (error) {
					throw handlePlacementRuntimeError(request)(error);
				}
			};

			return {
				middleware: koaMiddleware,
				endpoints: {
					renderer: koaMiddleware,
					data: dataMiddleware,
				},
				config: {
					...placementBackendConfig,
					...placementBackendParams,
					...placementConfig,
				},
				describe,
				runtime: standaloneRuntime,
			};
		},
	});

const applyDefaultMessageBackendStages = <T extends AnyValidatedBackendMessageStages>(
	stages: () => T,
): T => {
	const validatedDefaultStages: AnyValidatedBackendMessageStages = {};

	Object.entries(defaultBackendPlacementMessageStages).forEach(([stageName, stage]) => {
		validatedDefaultStages[stageName] = {
			validate: noValidation(),
			stage,
		};
	});

	return { ...validatedDefaultStages, ...stages() } as T;
};

export const createBackendPlacementRunners = <T extends AnyPlacementMessageStagesConfig>(
	config: T,
): T => {
	return ((placementMessageStages) => {
		const placementStages = config(placementMessageStages);

		const wrappedPlacementStages: Partial<AnyBackendPlacementStages> = {};

		Object.entries(placementStages).forEach(([stageName, stage]) => {
			if (!stage) {
				wrappedPlacementStages[stageName] = undefined;
			}

			wrappedPlacementStages[stageName] = createBackendPlacementRunner({ stageName, stage });
		});

		return wrappedPlacementStages;
	}) as T;
};

const createBackendPlacementRunner =
	({ stageName, stage }: { stageName: string; stage: AnyBackendPlacementStage }) =>
	async (params: AnyBackendPlacementStageInput) => {
		const request = params.request.location.tag({
			placementLevel: 'Placement',
			stageName,
		});

		if (!stage) {
			return params.data;
		}

		try {
			const result = await placementTimedStage(stage)({ request, data: params.data });
			return result;
		} catch (error) {
			throw handlePlacementError(request)(error);
		}
	};

export const createBackendPlacementMessageTemplateRouter = <T extends AnyBackendMessageStages>(
	config: CreateBackendPlacementMessageExecuteConfig<T>,
): Record<string, T> => {
	const result: Record<string, AnyBackendMessageStages> = {};

	config.messageTemplates.forEach(([messageTemplateId, stages]) =>
		Object.entries(stages).forEach(
			([stageName, stage]: [string, AnyValidatedBackendMessageStage]) => {
				if (!result[messageTemplateId]) result[messageTemplateId] = {};

				// We can safely use a non-null assertion here as we validate the existance of
				// result[messageTemplateId] above
				result[messageTemplateId][stageName] = createBackendPlacementMessageRunner({
					stage,
					stageName,
					placementId: config.placementId,
				});
			},
		),
	);

	return result as Record<string, T>;
};

const createBackendPlacementMessageRunner: CreateBackendPlacementMessageRunner = (config) => {
	return async (input) => {
		const { message } = input;

		const request = input.request.location.tag({
			placementLevel: 'Message',
			messageTemplateId: message?.messageTemplateId,
			messageInstanceId: message?.messageInstanceId,
			triggerId: message?.triggerId,
			stageName: config?.stageName,
		});

		try {
			const { validate, stage } = config.stage;

			validate(input.message.context); // Throws if fails validation

			const messagePerformance =
				request.messagePerformance ??
				new MessagePerformance(request, {
					trackEndToEnd: false,
					metricNameSegments: {
						flow: 'pull',
						placementId: config.placementId,
					},
				});

			const hydratedContext = await messagePerformance.measureOperation(
				async () => await messageTimedStage(stage)({ request, message }),
				{
					messageTemplateId: message.messageTemplateId,
					operation: config.stageName,
					stage: 'hydration',
				},
				{
					// This stage executes message-template hydration code owned by message makers.
					layer: 'message',
				},
			);

			// V2 render functions receive a context prop
			return {
				...message,
				context: {
					...hydratedContext,
					...(message.context.triggerType !== undefined
						? { triggerType: message.context.triggerType }
						: {}),
				},
			};
		} catch (error) {
			throw handlePlacementMessageError(request)(error);
		}
	};
};

export const createMessageTemplateByStageLookup = <T extends AnyValidatedStages>(config: {
	messageTemplates: Array<PlacementMessage<T>>;
}): Record<string, T> => {
	const result: Record<string, AnyValidatedStages> = {};

	config.messageTemplates.forEach(([messageTemplateId, stage]) => {
		Object.entries(stage).forEach(([stageName, stageExecutable]) => {
			if (!result[stageName]) result[stageName] = {};

			// We can safely use a non-null assertion here as we validate the existance of
			// result[stageName] above
			result[stageName][messageTemplateId] = stageExecutable;
		});
	});

	return result as Record<string, T>;
};
